// @ts-ignore
import { layoutProcess } from 'bpmn-auto-layout';

// @ts-ignore
import BpmnModdle from 'bpmn-moddle';

// @ts-ignore
import modelerModdle from 'modeler-moddle/resources/modeler.json';

const moddle = new BpmnModdle({ modeler: modelerModdle });

// @ts-ignore
import pkg from '../package.json';

const { version } = pkg;

const DEFAULT_EXECUTION_PLATFORM = 'Camunda Cloud',
      DEFAULT_EXECUTION_PLATFORM_VERSION = '8.6',
      DEFAULT_EXPORTER = 'Camunda BPMN Copilot',
      DEFAULT_EXPORTER_VERSION = version;

const TAG_NAMES = {
  ASSISTANT_TEXT: 'thinking',
  ASSISTANT_XML: 'BPMN_CODE',
  USER_TEXT: 'user_prompt',
  USER_XML: 'user_bpmn_code'
};

const MAX_MESSAGES_WITH_XML = 3;

const MAX_MESSAGES_LENGTH_ANY = 400_000;

export default class Copilot {

  /**
   * @param {import('@camunda/bpmn-copilot-client').CopilotConfiguration} configuration
   */
  constructor(configuration) {
    const {
      bpmnjs,
      invoke,
      preventBpmnJsEdit = false,
      executionPlatform = DEFAULT_EXECUTION_PLATFORM,
      executionPlatformVersion = DEFAULT_EXECUTION_PLATFORM_VERSION,
      exporter = DEFAULT_EXPORTER,
      exporterVersion = DEFAULT_EXPORTER_VERSION
    } = configuration;

    /** @type {import('bpmn-js/lib/Modeler').default} */
    this._bpmnjs = bpmnjs;

    /** @type {import('@camunda/bpmn-copilot-client').InvokeCallback} */
    this._invoke = invoke;

    /** @type {boolean} */
    this._preventBpmnJsEdit = preventBpmnJsEdit;

    /** @type {import('@camunda/bpmn-copilot-client').InternalMessage[]} */
    this._messages = [];

    this._executionPlatform = {
      name: executionPlatform,
      version: executionPlatformVersion
    };

    this._exporter = {
      name: exporter,
      version: exporterVersion
    };
  }

  /**
   * Invoke the copilot.
   *
   * @param {string} prompt
   * @param {import('@camunda/bpmn-copilot-client').Route} [route]
   *
   * @returns {Promise<import('@camunda/bpmn-copilot-client').InvokeResult>}
   */
  async invoke(prompt, route) {
    const xml = await this._getXML();

    if (!xml) {
      throw new Error('Error getting XML');
    }

    const newMessages = [
      {
        role: 'user',
        text: prompt,
        xml
      }
    ];

    const selected = this._getSelected();

    const controller = new AbortController();

    const invokeMessages = toInvokeMessages([ ...this._messages, ...newMessages ]);

    if (new TextEncoder().encode(JSON.stringify(invokeMessages)).length > MAX_MESSAGES_LENGTH_ANY) {
      throw new Error('Your prompts are too long. Please try again with a shorter prompt or simpler diagram.');
    }

    const response = this._invoke({
      messages: invokeMessages,
      model_id: 'anthropic.claude-3-5-sonnet-20240620-v1:0',
      selection: selected,
      route
    }, controller.signal)
      .then((response) => this._handleInvokeSuccess(response, newMessages, route))
      .catch(this._handleInvokeError);

    return {
      response,
      cancel: () => controller.abort()
    };
  }

  /**
   * Get messages.
   *
   * @returns {import('@camunda/bpmn-copilot-client').InternalMessage[]} messages
   */
  getMessages() {
    return this._messages;
  }

  /**
   * Fix XML by replacing definitions and creating DI.
   *
   * @param {string} xml
   *
   * @returns {Promise<string>}
   */
  async _fixXML(xml) {
    xml = await fixDefinitions(xml, this._executionPlatform, this._exporter);

    xml = await layoutProcess(xml);

    return xml;
  }

  /**
   * Get XML.
   *
   * @returns {Promise<string|undefined>}
   */
  async _getXML() {
    const { xml } = await this._bpmnjs.saveXML();

    return xml;
  }

  /**
   * Get IDs of selected elements.
   *
   * @returns {string[]}
   */
  _getSelected() {
    return this._bpmnjs.get('selection').get().map(({ id }) => id);
  }

  async _handleInvokeSuccess(response, newMessages, route) {

    // request valid, add user message
    this._messages.push(...newMessages);

    let message;

    try {
      ({ output_message: message } = await response.json());

      if (route === 'call_routing') {
        return message;
      }

      const { text: content } = message.content[ 0 ];

      if (route === 'call_chat') {
        const text = getTagContent(content, 'chat');
        this._messages.push({
          role: 'assistant',
          text,
          xml: null
        });
        return text;
      }

      await validateResponse(content);

      const text = getTagContent(content, TAG_NAMES.ASSISTANT_TEXT);

      if (this._preventBpmnJsEdit) {
        this._messages.push({
          role: 'assistant',
          text,
          xml: null
        });
        return text;
      }

      let xml = getTagContent(content, TAG_NAMES.ASSISTANT_XML);

      await validateXML(xml);

      // response valid, add assistant message
      this._messages.push({
        role: 'assistant',
        text,
        xml
      });

      xml = await this._fixXML(xml);

      const { warnings } = await this._bpmnjs.importXML(xml);

      if (warnings.length) {
        console.log(warnings);
      }

      this._bpmnjs.get('canvas').zoom('fit-viewport');

      return text;
    } catch (error) {
      console.error(error, 'response', response, 'message', message);

      this._bpmnjs.get('eventBus').fire('copilot.error', { error });

      // response invalid, add assistant message with error
      this._messages.push({
        role: 'assistant',
        text: 'There was an error processing your request. Please try again.',
        xml: null
      });

      return 'There was an error processing your request. Please try again.';
    }
  };

  _handleInvokeError(error) {
    if (error instanceof Error && error.name === 'AbortError') {

      // request cancelled, do not add user or assistant message
      return null;
    }

    throw error;
  };
}

function getTagContent(text, tag) {
  const regex = new RegExp(`<${ tag }>(.*?)</${ tag }>`, 's');

  const match = text.match(regex);

  return match ? match[ 1 ].trim() : null;
}

async function fixDefinitions(xml, executionPlatform, exporter) {
  const { rootElement: definitions } = await moddle.fromXML(xml);

  const diagrams = definitions.get('diagrams'),
        rootElements = definitions.get('rootElements');

  const newDefintions = moddle.create('bpmn:Definitions', {
    id: 'Definitions_1',
    targetNamespace: 'http://bpmn.io/schema/bpmn',
    executionPlatform: executionPlatform.name,
    executionPlatformVersion: executionPlatform.version,
    exporter: exporter.name,
    exporterVersion: exporter.version,
    rootElements,
    diagrams
  });

  for (const diagram of diagrams) {
    diagram.$parent = newDefintions;
  }

  for (const rootElement of rootElements) {
    rootElement.$parent = newDefintions;
  }

  const { xml: newXML } = await moddle.toXML(newDefintions, { format: true });

  return newXML;
}

async function validateResponse(text) {
  if (!getTagContent(text, 'thinking') || !getTagContent(text, 'BPMN_CODE')) {
    throw new Error('Invalid response');
  }
}

async function validateXML(xml) {
  let definitions;

  ({ rootElement: definitions } = await moddle.fromXML(xml));

  if (!definitions.get('rootElements').length
  || !definitions.get('rootElements').find(rootElement => rootElement.$instanceOf('bpmn:Process'))) {
    throw new Error('No process found');
  }
}

function toInvokeMessages(messages) {
  return messages.toReversed().map((message, index) => {
    const { role } = message;

    if (role === 'assistant') {
      return toAssistantInvokeMessage(message, index < MAX_MESSAGES_WITH_XML);
    } else if (role === 'user') {
      return toUserInvokeMessage(message, index < MAX_MESSAGES_WITH_XML);
    }

    throw new Error('Invalid role');
  }).toReversed();
};

function toAssistantInvokeMessage(message, addXML) {
  let text = `<${ TAG_NAMES.ASSISTANT_TEXT }>${ message.text }</${ TAG_NAMES.ASSISTANT_TEXT }>`;

  if (addXML) {
    text = text.concat(`<${ TAG_NAMES.ASSISTANT_XML }>${ message.xml }</${ TAG_NAMES.ASSISTANT_XML }>`);
  }

  return {
    role: 'assistant',
    content: [
      {
        text
      }
    ]
  };
}

function toUserInvokeMessage(message, addXML) {
  let text = `<${ TAG_NAMES.USER_TEXT }>${ message.text }</${ TAG_NAMES.USER_TEXT }>`;

  if (addXML) {
    text = text.concat(`<${ TAG_NAMES.USER_XML }>${ message.xml }</${ TAG_NAMES.USER_XML }>`);
  }

  return {
    role: 'user',
    content: [
      {
        text
      }
    ]
  };
}